import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import useAuth from "../../hooks/useAuth";
import { getOptionsDirection } from "../../utils/hasCodeChars";

const EXAM_DURATION_SECONDS = 60 * 60;

const FinalAssessment = ({ courseData, onRetake }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [examStarted, setExamStarted] = useState(() => {
    // Only consider exam started if the user previously clicked start
    return localStorage.getItem(`exam_started_${courseData.id}`) === "true";
  });

  const [timeLeft, setTimeLeft] = useState(() => {
    // Only restore timer if exam was actually started
    return localStorage.getItem(`exam_started_${courseData.id}`) === "true"
      ? parseInt(localStorage.getItem(`exam_timer_${courseData.id}`), 10) || EXAM_DURATION_SECONDS
      : EXAM_DURATION_SECONDS;
  });
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionsLength, setQuestionsLength] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finalResult, setFinalResult] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [certLang, setCertLang] = useState(null);
  const [retaking, setRetaking] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const selectedQuestions = useMemo(() => {
    if (!questions.length) return [];
    return questions.map((q) => ({ ...q, options: q.options || [] }));
  }, [questions]);

  const currentQuestion = selectedQuestions[currentIndex];

  /* =========================
     FETCH QUESTIONS
  ========================== */
  const fetchQuestions = async () => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/course-exam/${courseData.id}`, {
        headers: { Authorization: `Bearer ${user?.token}`, lang: i18n.language },
      });

      const allQuestions = res.data.data.questions || [];
      // Filter out already answered questions
      const unsolvedQuestions = allQuestions.filter((q) => q.answer === null);
      setQuestions(unsolvedQuestions);
      setQuestionsLength(allQuestions.length);

      // If all questions already answered, fetch result directly
      if (unsolvedQuestions.length === 0 || res.data.data.last_status === 2) {
        await fetchQuestionsResult();
      }

      setLoadingAssessment(false);
    } catch (err) {
      console.error("Failed to load assessment", err);
      if (err.response?.status === 401) navigate("/auth/login", { replace: true });
    }
  };

  /* =========================
     FETCH FINAL RESULT
  ========================== */
  const fetchQuestionsResult = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/exam-result/${courseData.id}`, {
        headers: { Authorization: `Bearer ${user?.token}`, lang: i18n.language },
      });
      setFinalResult(res.data.data);
      setLoadingAssessment(false);
    } catch (err) {
      console.error("Failed to load assessment", err);
      if (err.response?.status === 401) navigate("/auth/login", { replace: true });
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!questions.length) return;
    setCurrentIndex(0);
  }, [questions]);

  useEffect(() => {
    const handleLogout = async () => {
      if (examStarted && !finalResult) {
        await finishAssessment(); // auto-submit the exam
      }
      // Reset everything
      localStorage.removeItem(`exam_timer_${courseData.id}`);
      localStorage.removeItem(`exam_started_${courseData.id}`);
      localStorage.removeItem(`exam_end_time_${courseData.id}`);
      setExamStarted(false);
      setAnswers([]);
      setQuestions([]);
      setFinalResult(null);
    };

    window.addEventListener("logout_finish_exam", handleLogout);

    return () => window.removeEventListener("logout_finish_exam", handleLogout);
  }, [examStarted, finalResult]);

  useEffect(() => {
    if (!examStarted || finalResult) return;

    const tick = () => {
      const endTime = parseInt(localStorage.getItem(`exam_end_time_${courseData.id}`), 10);
      const now = Date.now();
      const remaining = Math.floor((endTime - now) / 1000);

      if (remaining <= 0) {
        finishAssessment();
        return;
      }

      setTimeLeft(remaining);
    };

    tick(); // set initial value immediately
    const timer = setInterval(tick, 1000);

    return () => clearInterval(timer);
  }, [examStarted, finalResult]);

  const resetTimer = () => {
    const initialTime = EXAM_DURATION_SECONDS;
    setTimeLeft(initialTime);
    localStorage.setItem(`exam_timer_${courseData.id}`, initialTime);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  /* =========================
     STORE ANSWERS LOCALLY
  ========================== */
  const selectAnswer = (selectedIndex) => {
    if (!currentQuestion) return;
    const newAnswers = [...answers];
    newAnswers[currentIndex] = { questionId: currentQuestion.id, selectedIndex };
    setAnswers(newAnswers);
  };

  const submitSurvey = async () => {
    setSubmittingSurvey(true);

    const payload = {
      questions: Object.entries(surveyAnswers).map(([id, answer]) => ({
        id,
        answer,
      })),
    };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/solving-surveys/${finalResult.survey.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSurveySubmitted(true);
    } catch (err) {
      console.error("Failed to submit survey", err.response?.data || err);
    } finally {
      setSubmittingSurvey(false);
    }
  };

  const handleSurveyChange = (questionId, value) => {
    setSurveyAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  /* =========================
     SUBMIT SINGLE ANSWER
  ========================== */
  const submitAnswer = async (question, selectedOption) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/submit-course-exam/${courseData.id}`,
        { question_id: question.id, answer: selectedOption.id },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
    } catch (err) {
      console.error("Failed to submit answer", err.response?.data || err);
    }
  };

  /* =========================
     FINISH EXAM
  ========================== */
  const finishAssessment = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // submit last answer
      const lastAnswer = answers[currentIndex];
      if (lastAnswer) {
        const selectedOption = currentQuestion.options[lastAnswer.selectedIndex];
        await submitAnswer(currentQuestion, selectedOption);
      }

      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/finish-course-exam/${courseData.id}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${user?.token}` },
      });

      localStorage.removeItem(`exam_timer_${courseData.id}`);
      localStorage.removeItem(`exam_started_${courseData.id}`);
      localStorage.removeItem(`exam_end_time_${courseData.id}`);
      await fetchQuestionsResult();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };
  /* =========================
     RETAKE EXAM
  ========================== */

  const handleRetake = async () => {
    setRetaking(true);
    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/retake-exam/${courseData.id}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${user?.token}` },
      });

      if (typeof onRetake === "function") onRetake();

      // Reset state
      setFinalResult(null);
      setQuestions([]);
      setAnswers([]);
      setExamStarted(false);
      localStorage.removeItem(`exam_started_${courseData.id}`);
      localStorage.removeItem(`exam_end_time_${courseData.id}`);
      resetTimer(); // reset timer for new attempt

      await fetchQuestions();
    } catch (err) {
      console.error("Failed to retake exam", err.response?.data || err);
    } finally {
      setRetaking(false);
    }
  };

  /* =========================
     DOWNLOAD CERTIFICATE
  ========================== */
  const downloadCertificate = async (lang) => {
    if (!finalResult) return;
    setCertLang(lang);
    setDownloading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/course-exam-certificate/${courseData.id}`,
        {
          params: { cer_lang: lang },
          responseType: "blob",
          headers: { Accept: "application/json", Authorization: `Bearer ${user?.token}` },
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const label = lang === "ar" ? "شهادة إتمام مسار" : "Course Completion Certificate";
      link.setAttribute("download", `${label} ${courseData?.name || ""}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download certificate", err.response?.data || err);
    } finally {
      setDownloading(false);
      setCertLang(null);
    }
  };

  /* =========================
     LOADING STATES
  ========================== */
  if (loadingAssessment)
    return (
      <div className="inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
        <svg
          className="h-10 w-10 animate-spin text-purple-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
          <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );

  if (submitting)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-10 py-8 shadow-2xl">
          <svg
            className="h-12 w-12 animate-spin text-purple-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <p className="text-sm font-semibold text-gray-700">{t("submitting_assessment")}</p>
        </div>
      </div>
    );

  /* =========================
     FINAL RESULT / CERTIFICATE UI
  ========================== */
  if (finalResult) {
    const perfectScore = finalResult.correct_answers === finalResult.total_answers;
    const surveyExists = finalResult.survey;

    return (
      <div className="flex flex-col items-center gap-6 rounded-lg bg-green-50 p-8 text-center">
        <h2 className="text-2xl font-bold text-green-700">تم الانتهاء من الاختبار</h2>
        <p className="text-gray-700">
          نتيجتك: {finalResult.correct_answers} / {finalResult.total_answers}
        </p>

        {!surveySubmitted && surveyExists && (
          <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-md">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">استبيان تقييم الدورة</h3>
            {surveyExists.questions.map((q) => {
              const isRequired = q.type !== "textarea"; // all except textarea are required
              return (
                <div key={q.id} className="mb-4">
                  <label className="mb-2 block font-semibold">
                    {q.title} {isRequired && <span className="text-red-500">*</span>}
                  </label>

                  {q.type === "stars" ? (
                    <div className="flex items-center justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleSurveyChange(q.id, star)}
                          className={`rounded px-3 py-1 ${
                            surveyAnswers[q.id] >= star ? "bg-yellow-400" : "bg-gray-200"
                          }`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={surveyAnswers[q.id] || ""}
                      onChange={(e) => handleSurveyChange(q.id, e.target.value)}
                      className="w-full rounded border p-2"
                    />
                  )}
                </div>
              );
            })}

            <button
              onClick={submitSurvey}
              disabled={
                submittingSurvey ||
                surveyExists.questions.some(
                  (q) => q.type !== "textarea" && !surveyAnswers[q.id] // required not answered
                )
              }
              className={`mt-4 rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400`}
            >
              {submittingSurvey ? "جارٍ الإرسال..." : "إرسال الاستبيان"}
            </button>
          </div>
        )}

        {(!surveyExists || surveySubmitted) && (
          <div className="mt-4 flex flex-col gap-4">
            {perfectScore ? (
              <p className="text-gray-700">🎉 لقد حصلت على الدرجة الكاملة! يمكنك الآن تنزيل الشهادة.</p>
            ) : (
              <button
                onClick={handleRetake}
                disabled={retaking}
                className={`rounded-lg px-6 py-3 text-white ${retaking ? "cursor-not-allowed bg-gray-400" : "bg-red-600 hover:bg-red-700"}`}
              >
                {retaking ? "جارٍ إعادة الاختبار..." : "إعادة الاختبار"}
              </button>
            )}

            {perfectScore && (
              <div className="w-full max-w-xl rounded-xl border border-green-100 bg-white p-6 shadow-md">
                <div className="mb-5 flex items-center gap-2 text-green-700">
                  <span className="text-2xl">🏆</span>
                  <h3 className="text-lg font-bold">تحميل الشهادة</h3>
                </div>
                <p className="mb-5 text-sm text-gray-500 text-right">اختر لغة الشهادة التي تريد تحميلها</p>
                <div className="grid grid-cols-2 gap-4">
                  {/* Arabic */}
                  <button
                    onClick={() => downloadCertificate("ar")}
                    disabled={downloading}
                    className={`group relative flex flex-col items-center gap-3 rounded-xl border-2 p-5 transition-all duration-200
                      ${downloading && certLang === "ar"
                        ? "cursor-not-allowed border-gray-200 bg-gray-50"
                        : "border-green-200 bg-green-50 hover:border-green-500 hover:bg-green-100 hover:shadow-md"
                      }`}
                  >
                    <span className="font-bold text-gray-800">العربية</span>
                    <span className={`mt-1 rounded-full px-4 py-1.5 text-sm font-semibold text-white transition-colors
                      ${downloading && certLang === "ar" ? "bg-gray-400" : "bg-green-600 group-hover:bg-green-700"}`}>
                      {downloading && certLang === "ar" ? "جارٍ التحميل..." : "تحميل"}
                    </span>
                  </button>

                  {/* English */}
                  <button
                    onClick={() => downloadCertificate("en")}
                    disabled={downloading}
                    className={`group relative flex flex-col items-center gap-3 rounded-xl border-2 p-5 transition-all duration-200
                      ${downloading && certLang === "en"
                        ? "cursor-not-allowed border-gray-200 bg-gray-50"
                        : "border-blue-200 bg-blue-50 hover:border-blue-500 hover:bg-blue-100 hover:shadow-md"
                      }`}
                  >
                    <span className="font-bold text-gray-800">English</span>
                    <span className={`mt-1 rounded-full px-4 py-1.5 text-sm font-semibold text-white transition-colors
                      ${downloading && certLang === "en" ? "bg-gray-400" : "bg-blue-600 group-hover:bg-blue-700"}`}>
                      {downloading && certLang === "en" ? "Downloading..." : "Download"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* =========================
   START EXAM SCREEN
========================= */
  if (!examStarted && !finalResult && questionsLength === questions.length) {
    return (
      <div className="mx-auto max-w-3xl pb-10 text-center leading-8">
        <h2 className="mb-6 text-3xl font-bold text-gray-800">
          {courseData.id == 3
            ? "الاختبار النهائي – مسار تطوير تطبيقات أندرويد"
            : courseData.id == 4
            ? "الاختبار النهائي – مسار أساسيات البرمجة"
            : courseData.id == 5
            ? "الاختبار النهائي – مسار تحليل البيانات"
            : "الاختبار النهائي – مسار الذكاء الاصطناعي"}
        </h2>

        <p className="text-gray-700">
          {courseData.id == 3 ? (
            <>
              يمثل هذا الاختبار التقييم النهائي للمسار ويهدف إلى قياس مدى إتقانك للمفاهيم والمهارات التي
              تناولتها خلال الوحدات السابقة، يغطي الاختبار الموضوعات الأساسية المتعلقة بأساسيات تطوير تطبيقات
              أندرويد. يمنحك اجتياز هذا الاختبار فرصة الحصول على{" "}
              <span className="font-semibold text-green-600">شهادة إتمام المسار</span> والتي تعكس قدرتك على
              فهم مفاهيم لغة كوتلين وتطوير تطبيقات أندرويد الأساسية.
            </>
          ) : courseData.id == 4 ? (
            <>
              يمثل هذا الاختبار التقييم النهائي للمسار ويهدف إلى قياس مدى إتقانك للمفاهيم والمهارات الأساسية
              التي تم تناولها خلال الوحدات السابقة، حيث يغطي الاختبار أساسيات تطوير الويب باستخدام HTML وCSS
              وJavaScript وبناء صفحات ويب تفاعلية وديناميكية. يمنحك اجتياز هذا الاختبار فرصة الحصول على{" "}
              <span className="font-semibold text-green-600">شهادة إتمام المسار</span> والتي تعكس قدرتك على
              فهم أساسيات البرمجة وتطوير صفحات ويب متكاملة.
            </>
          ) : courseData.id == 5 ? (
            <>
              يمثل هذا الاختبار التقييم النهائي للمسار ويهدف إلى قياس مدى إتقانك للمفاهيم والمهارات التي
              تناولتها خلال الوحدات السابقة، يغطي الاختبار الموضوعات الأساسية المتعلقة بأساسيات تحليل البيانات.
              يمنحك اجتياز هذا الاختبار فرصة الحصول على{" "}
              <span className="font-semibold text-green-600">شهادة إتمام المسار</span> والتي تعكس قدرتك على
              فهم مفاهيم وأساسيات تحليل البيانات والتعامل معها.
            </>
          ) : (
            <>
              يمثل هذا الاختبار التقييم النهائي للمسار ويهدف إلى قياس مدى إتقانك للمفاهيم والمهارات التي
              تناولتها خلال الوحدات السابقة، يغطي الاختبار الموضوعات الأساسية المتعلقة بأساسيات الذكاء
              الاصطناعي واستخدام أدوات الذكاء الاصطناعي التوليدي مثل ChatGPT، إضافة إلى المبادئ الأخلاقية
              المرتبطة باستخدام هذه التقنيات. يمنحك اجتياز هذا الاختبار فرصة الحصول على{" "}
              <span className="font-semibold text-green-600">شهادة إتمام المسار</span> والتي تعكس قدرتك على
              فهم مفاهيم الذكاء الاصطناعي الأساسية واستخدام أدواته بطريقة واعية ومسؤولة.
            </>
          )}
        </p>

        <p className="mt-6 text-gray-700">يرجى قراءة التعليمات التالية بعناية قبل البدء بالاختبار:</p>

        <h3 className="mt-6 text-xl font-semibold text-gray-800">تعليمات الاختبار</h3>

        <ul className="mt-4 list-inside list-disc space-y-2 text-right text-gray-700">
          <li className="text-right">
            يتكوّن الاختبار من{" "}
            <span className="font-bold text-blue-600">{courseData.id == 3 ? "15 سؤالًا" : courseData.id == 4 ? "16 سؤالًا" : "12 سؤالًا"}</span>{" "}
            تغطي المفاهيم والمهارات الأساسية في المسار.
          </li>

          <li className="text-right">
            <span className="font-semibold text-blue-600">مدة الاختبار 60 دقيقة</span> تبدأ عند بدء المحاولة.
          </li>

          <li className="text-right">
            للحصول على <span className="font-semibold text-green-600">شهادة إتمام المسار</span> يجب الإجابة
            على <span className="font-semibold text-red-600">جميع الأسئلة بشكل صحيح.</span>
          </li>

          <li className="text-right">
            في حال عدم اجتياز الاختبار، يمكنك{" "}
            <span className="font-semibold text-orange-600">إعادة المحاولة مرة أخرى</span> حتى تحقق النتيجة
            المطلوبة.
          </li>
        </ul>

        <p className="mt-4 text-gray-700">
          نوصي بمراجعة محتوى الوحدات والأنشطة التعليمية قبل البدء بالاختبار لضمان أفضل أداء.
        </p>

        <p className="mt-8 text-lg font-semibold text-indigo-600">نتمنى لك التوفيق.</p>

        <button
          onClick={() => {
            setExamStarted(true);
            const now = Date.now();
            const duration = EXAM_DURATION_SECONDS * 1000; // 60 minutes
            const endTime = now + duration;

            localStorage.setItem(`exam_end_time_${courseData.id}`, endTime);
            localStorage.setItem(`exam_started_${courseData.id}`, "true");
            localStorage.setItem("current_exam_course_id", courseData.id);
            setTimeLeft(Math.floor(duration / 1000));

            // store for logout
            resetTimer();
          }}
          className="mt-4 rounded-lg bg-purple-600 px-6 py-3 text-white hover:bg-purple-700"
        >
          بدء الاختبار
        </button>
      </div>
    );
  }

  /* =========================
     MAIN EXAM UI
  ========================== */
  const solvedCount = questionsLength - selectedQuestions.length;
  return (
    <div className="prose relative mx-auto w-full max-w-3xl rounded-3xl">
      {/* Question Progress */}
      <div className="flex items-center justify-between">
        <div className="mb-2 text-right text-sm text-gray-500">
          سؤال {solvedCount + currentIndex + 1} من {questionsLength}{" "}
        </div>

        <div className="mb-4 flex items-center justify-center gap-1 text-right text-lg font-semibold text-red-600">
          <span>{formatTime(timeLeft)}</span>
          <FiClock className="mt-[2px]" />
        </div>
      </div>

      <h3 className="mb-4 whitespace-pre-line text-2xl font-bold leading-normal text-gray-800">
        {
          <div
            dangerouslySetInnerHTML={{
              __html: currentQuestion?.question?.replace(/\r\n/g, "<br />"),
            }}
          />
        }
      </h3>

      <div className="grid gap-4">
        {(() => {
          const opts = currentQuestion?.options || [];
          const dir = getOptionsDirection(opts);
          return opts.map((opt, idx) => {
            const isSelected = answers[currentIndex]?.selectedIndex === idx;
            return (
              <label
                key={opt.id || idx}
                className={`flex cursor-pointer items-center gap-3 rounded-md p-3 font-medium transition hover:bg-purple-50`}
              >
                <input
                  type="radio"
                  name={`question-${currentQuestion.id}`}
                  checked={isSelected}
                  onChange={() =>
                    setAnswers((prev) => {
                      const copy = [...prev];
                      copy[currentIndex] = { selectedIndex: idx };
                      return copy;
                    })
                  }
                  className="h-6 w-6 accent-purple-500"
                />

                <span
                  className="flex-1 whitespace-pre-line break-words"
                  dir={dir}
                  style={{
                    textAlign: dir === "ltr" ? "left" : "right",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                  dangerouslySetInnerHTML={{ __html: (opt.option || "").replace(/</g, "&lt;").replace(/>/g, "&gt;") }}
                />
              </label>
            );
          });
        })()}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-500">{t("select_answer_warning")}</p>

        {currentIndex < selectedQuestions.length - 1 ? (
          <button
            onClick={async () => {
              if (nextLoading) return; // extra protection

              setNextLoading(true);
              try {
                const selected = answers[currentIndex];
                if (selected) {
                  const selectedOption = currentQuestion.options[selected.selectedIndex];
                  await submitAnswer(currentQuestion, selectedOption);
                }
                setCurrentIndex((prev) => prev + 1);
              } finally {
                setNextLoading(false);
              }
            }}
            disabled={answers[currentIndex] === undefined || nextLoading}
            className={`flex h-12 min-w-36 items-center justify-center rounded-md px-6 py-3 text-center text-white ${
              answers[currentIndex] === undefined || nextLoading
                ? "cursor-not-allowed bg-purple-600 opacity-50"
                : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {nextLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-purple-600"></div>
            ) : (
              t("nextQuestion")
            )}
          </button>
        ) : (
          <button
            onClick={finishAssessment}
            disabled={answers[currentIndex] === undefined}
            className={`h-12 min-w-36 rounded-lg px-6 py-3 text-white ${answers[currentIndex] === undefined ? "cursor-not-allowed bg-green-600 opacity-50" : "bg-green-600 hover:bg-green-700"}`}
          >
            {t("Submit")}
          </button>
        )}
      </div>
    </div>
  );
};

export default FinalAssessment;
